
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import * as rolesApi from "@/services/rolesApi";
import { RoleDTO } from "@/types/api";
import { Role, PermissionLevel } from "./types";

// Map backend RoleDTO to the UI Role shape
function mapToRole(dto: RoleDTO): Role {
  return {
    id: dto.id || '',
    name: dto.name,
    description: dto.description || null,
    is_system: false,
    created_at: '',
    updated_at: '',
  };
}

export const useRoles = () => {
  return useQuery({
    queryKey: ["roles"],
    queryFn: async () => {
      const roles = await rolesApi.listRoles();
      return roles.map(mapToRole);
    },
  });
};

export const useCreateRole = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ 
      name, 
      description, 
      permissions 
    }: { 
      name: string, 
      description?: string,
      permissions?: { resource_id: string, permission: PermissionLevel }[]
    }) => {
      const privileges = permissions?.map(p => ({
        code: p.resource_id,
        description: p.permission,
      }));
      
      return rolesApi.createRole({ name, description, privileges });
    },
    onSuccess: () => {
      toast.success("Role created successfully");
      queryClient.invalidateQueries({ queryKey: ["roles"] });
      queryClient.invalidateQueries({ queryKey: ["role_permissions"] });
    },
    onError: (error) => {
      toast.error(`Failed to create role: ${error.message}`);
    }
  });
};

export const useUpdateRole = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, name, description }: { id: string, name: string, description?: string }) => {
      return rolesApi.updateRole({ id, name, description });
    },
    onSuccess: () => {
      toast.success("Role updated successfully");
      queryClient.invalidateQueries({ queryKey: ["roles"] });
    },
    onError: (error) => {
      toast.error(`Failed to update role: ${error.message}`);
    }
  });
};

export const useDeleteRole = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, name }: { id: string; name?: string }) => {
      // The backend deletes by name, not by id
      if (!name) throw new Error('Role name is required for deletion');
      return rolesApi.deleteRole(name);
    },
    onSuccess: () => {
      toast.success("Role deleted successfully");
      queryClient.invalidateQueries({ queryKey: ["roles"] });
    },
    onError: (error) => {
      toast.error(`Failed to delete role: ${error.message}`);
    }
  });
};
